import React, { createContext, useEffect, useState, useCallback } from 'react';
import { fetchAuthSession, signOut, getCurrentUser, type AuthUser } from 'aws-amplify/auth';
import { configureAmplify } from './amplify-config';
import { getCurrentUser as fetchCurrentUserProfile } from '@/api/users-api';
import { type RolePermissions, getPermissions, DEFAULT_PERMISSIONS } from '@/lib/roles';
import type { MenuItem } from '@/api/types';
import { useCompanyContextStore } from '@/store/company-context-store';

configureAmplify();

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  companyId: string | null;
  roleId: string | null;
  roleName: string | null;
  permissions: RolePermissions;
  menus: MenuItem[];
  policyAcceptedAt: string | null;
  policyVersion: string | null;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  accessToken: null,
  companyId: null,
  roleId: null,
  roleName: null,
  permissions: DEFAULT_PERMISSIONS,
  menus: [],
  policyAcceptedAt: null,
  policyVersion: null,
  refreshAuth: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [roleName, setRoleName] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<RolePermissions | null>(null);
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [policyAcceptedAt, setPolicyAcceptedAt] = useState<string | null>(null);
  const [policyVersion, setPolicyVersion] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString() ?? null;

      // Fetch user profile with role, permissions, and menus from API.
      // The user is set only after the profile resolves: setting it first
      // renders an authenticated user without roleId, and ProtectedRoute
      // flashes the first-time SetupPage on every login.
      try {
        const profile = await fetchCurrentUserProfile();
        setUser(currentUser);
        setAccessToken(token);
        setCompanyId(profile.companyId);
        setRoleId(profile.roleId);
        setRoleName(profile.role?.name ?? null);
        setPermissions(profile.permissions);
        setMenus(profile.menus);
        setPolicyAcceptedAt(profile.policyAcceptedAt);
        setPolicyVersion(profile.policyVersion);

        // Set company context in global store for API client (include name for header display)
        useCompanyContextStore.getState().setCompany(profile.companyId, profile.companyName);
      } catch {
        setUser(currentUser);
        setAccessToken(token);
        setCompanyId(null);
        setRoleId(null);
        setRoleName(null);
        setPermissions(null);
        setMenus([]);
        setPolicyAcceptedAt(null);
        setPolicyVersion(null);
        useCompanyContextStore.getState().reset();
      }
    } catch {
      setUser(null);
      setAccessToken(null);
      setCompanyId(null);
      setRoleId(null);
      setRoleName(null);
      setPermissions(null);
      setMenus([]);
      setPolicyAcceptedAt(null);
      setPolicyVersion(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    setUser(null);
    setAccessToken(null);
    setCompanyId(null);
    setRoleId(null);
    setRoleName(null);
    setPermissions(null);
    setMenus([]);
    setPolicyAcceptedAt(null);
    setPolicyVersion(null);
    // Clear company context store
    useCompanyContextStore.getState().reset();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        accessToken,
        companyId,
        roleId,
        roleName,
        permissions: getPermissions(permissions),
        menus,
        policyAcceptedAt,
        policyVersion,
        refreshAuth: checkAuth,
        signOut: handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
